(() => {
  const initializedForms = new WeakSet();

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  function toAbsoluteUrl(path) {
    if (!path) return '';
    if (/^https?:\/\//i.test(path)) return path;
    return `${window.location.origin}${path}`;
  }

  class QuoteRequestForm {
    constructor(container) {
      this.container = container;
      this.catalogGroup = container.querySelector('[data-quote-catalog-group]');
      this.searchInput = container.querySelector('[data-quote-product-search]');
      this.searchResults = container.querySelector('[data-quote-search-results]');
      this.selectedList = container.querySelector('[data-quote-selected-products]');
      this.selectedEmpty = container.querySelector('[data-quote-selected-empty]');
      this.catalogOutput = container.querySelector('[data-quote-catalog-output]');
      this.shippingOptions = container.querySelectorAll('[data-quote-shipping-option]');
      this.shippingAccountWrapper = container.querySelector('[data-quote-shipping-account]');
      this.shippingAccountInput = container.querySelector('[data-quote-shipping-account-input]');

      this.selectedProducts = [];
      this.abortController = null;
      this.searchTimeout = null;
      this.variantsCache = new Map();

      this.handleDocumentClick = this.handleDocumentClick.bind(this);

      this.bindShippingEvents();
      this.updateShippingAccountVisibility();

      if (!this.searchInput || !this.searchResults || !this.selectedList || !this.catalogOutput) {
        return;
      }

      this.bindCatalogEvents();
      this.syncCatalogOutput();
    }

    bindShippingEvents() {
      if (!this.shippingOptions.length) return;

      this.shippingOptions.forEach((option) => {
        option.addEventListener('change', () => this.updateShippingAccountVisibility());
      });
    }

    updateShippingAccountVisibility() {
      if (!this.shippingAccountWrapper || !this.shippingAccountInput || !this.shippingOptions.length) return;

      const selectedOption = Array.from(this.shippingOptions).find((option) => option.checked);
      const showAccountField = selectedOption && selectedOption.value === 'Bill shipping to customer account';

      this.shippingAccountWrapper.classList.toggle('quote-request__hidden', !showAccountField);
      this.shippingAccountInput.disabled = !showAccountField;

      if (!showAccountField) {
        this.shippingAccountInput.value = '';
      }
    }

    bindCatalogEvents() {
      this.searchInput.addEventListener('input', (event) => {
        const query = event.target.value.trim();
        window.clearTimeout(this.searchTimeout);

        this.searchTimeout = window.setTimeout(() => {
          this.searchCatalogProducts(query);
        }, 250);
      });

      this.searchInput.addEventListener('focus', () => {
        if (this.searchResults.innerHTML.trim() !== '') {
          this.searchResults.hidden = false;
        }
      });

      this.searchResults.addEventListener('click', (event) => this.handleSearchResultClick(event));
      this.selectedList.addEventListener('input', (event) => this.handleQuantityInput(event));
      this.selectedList.addEventListener('change', (event) => this.handleSelectedListChange(event));
      this.selectedList.addEventListener('click', (event) => this.handleSelectedListClick(event));
      document.addEventListener('click', this.handleDocumentClick);
    }

    searchCatalogProducts(query) {
      if (query.length < 2) {
        this.clearSearchResults();
        return;
      }

      if (this.abortController) {
        this.abortController.abort();
      }

      this.abortController = new AbortController();

      const searchPath =
        window.routes && window.routes.predictive_search_url ? window.routes.predictive_search_url : '/search/suggest';
      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'product',
        'resources[limit]': '8',
        'resources[options][unavailable_products]': 'hide',
      });

      this.searchResults.hidden = false;
      this.searchResults.innerHTML = '<p class="quote-request__search-message">Searching products...</p>';

      fetch(`${searchPath}.json?${params.toString()}`, {
        signal: this.abortController.signal,
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(String(response.status));
          }
          return response.json();
        })
        .then((payload) => {
          if (this.searchInput.value.trim() !== query) return;

          const products =
            payload &&
            payload.resources &&
            payload.resources.results &&
            Array.isArray(payload.resources.results.products)
              ? payload.resources.results.products
              : [];

          this.renderSearchResults(products);
        })
        .catch((error) => {
          if (error && error.name === 'AbortError') return;

          this.searchResults.hidden = false;
          this.searchResults.innerHTML =
            '<p class="quote-request__search-message">Unable to load products right now. Please try again.</p>';
        });
    }

    renderSearchResults(products) {
      if (!products.length) {
        this.searchResults.hidden = false;
        this.searchResults.innerHTML = '<p class="quote-request__search-message">No matching products found.</p>';
        return;
      }

      const itemsMarkup = products
        .map((product) => {
          const productId = Number(product.id);
          const isSelected = this.selectedProducts.some((selected) => selected.id === productId);
          const actionLabel = isSelected ? 'Added' : 'Add';
          const disabledAttribute = isSelected ? 'disabled aria-disabled="true"' : '';

          return `
            <li class="quote-request__results-item">
              <button
                type="button"
                class="quote-request__results-button"
                data-quote-result
                data-product-id="${productId}"
                data-product-title="${escapeHtml(product.title)}"
                data-product-url="${escapeHtml(product.url)}"
                ${disabledAttribute}
              >
                <span>${escapeHtml(product.title)}</span>
                <span class="quote-request__results-meta">${actionLabel}</span>
              </button>
            </li>
          `;
        })
        .join('');

      this.searchResults.hidden = false;
      this.searchResults.innerHTML = `<ul class="quote-request__results-list" role="listbox">${itemsMarkup}</ul>`;
    }

    handleSearchResultClick(event) {
      const addButton = event.target.closest('[data-quote-result]');
      if (!addButton || addButton.disabled) return;

      const productId = Number(addButton.dataset.productId);
      const existingProduct = this.selectedProducts.find((product) => product.id === productId);
      let addedProduct = null;

      if (existingProduct) {
        existingProduct.quantity += 1;
      } else {
        addedProduct = {
          id: productId,
          title: addButton.dataset.productTitle || 'Product',
          url: addButton.dataset.productUrl || '',
          quantity: 1,
          variantsLoading: true,
          variantOptions: [],
          selectedVariantId: '',
          selectedVariantTitle: '',
          variantLoadError: false,
        };

        this.selectedProducts.push(addedProduct);
      }

      this.renderSelectedProducts();
      this.syncCatalogOutput();

      if (addedProduct) {
        this.loadVariantsForProduct(addedProduct);
      }

      this.searchInput.value = '';
      this.clearSearchResults();
      this.searchInput.focus();
    }

    normalizeVariantTitle(title) {
      const cleanedTitle = typeof title === 'string' ? title.trim() : '';
      if (!cleanedTitle || cleanedTitle === 'Default Title') return 'Default';
      return cleanedTitle;
    }

    getProductJsonUrl(productUrl) {
      if (!productUrl) return '';

      try {
        const parsedUrl = new URL(productUrl, window.location.origin);
        const normalizedPath = parsedUrl.pathname.replace(/\/$/, '');
        if (!normalizedPath) return '';
        return normalizedPath.endsWith('.js') ? normalizedPath : `${normalizedPath}.js`;
      } catch (error) {
        return '';
      }
    }

    loadVariantsForProduct(product) {
      const cachedVariantData = this.variantsCache.get(product.id);
      if (cachedVariantData) {
        this.applyVariantData(product.id, cachedVariantData);
        return;
      }

      const productJsonUrl = this.getProductJsonUrl(product.url);
      if (!productJsonUrl) {
        this.applyVariantData(
          product.id,
          {
            variantOptions: [{ id: 'default', title: 'Default', available: true }],
            defaultVariantId: 'default',
            defaultVariantTitle: 'Default',
          },
          true
        );
        return;
      }

      fetch(productJsonUrl)
        .then((response) => {
          if (!response.ok) throw new Error(String(response.status));
          return response.json();
        })
        .then((productPayload) => {
          const productVariants = Array.isArray(productPayload.variants) ? productPayload.variants : [];
          const variantOptions = productVariants.map((variant) => ({
            id: String(variant.id),
            title: this.normalizeVariantTitle(variant.title),
            available: Boolean(variant.available),
          }));

          if (!variantOptions.length) {
            variantOptions.push({ id: 'default', title: 'Default', available: true });
          }

          const defaultVariant = variantOptions.find((variant) => variant.available) || variantOptions[0];
          const variantData = {
            variantOptions,
            defaultVariantId: defaultVariant.id,
            defaultVariantTitle: defaultVariant.title,
          };

          this.variantsCache.set(product.id, variantData);
          this.applyVariantData(product.id, variantData);
        })
        .catch(() => {
          this.applyVariantData(
            product.id,
            {
              variantOptions: [{ id: 'default', title: 'Default', available: true }],
              defaultVariantId: 'default',
              defaultVariantTitle: 'Default',
            },
            true
          );
        });
    }

    applyVariantData(productId, variantData, variantLoadError = false) {
      const selectedProduct = this.selectedProducts.find((product) => product.id === productId);
      if (!selectedProduct) return;

      const normalizedOptions = Array.isArray(variantData.variantOptions) ? variantData.variantOptions : [];
      const selectedVariant =
        normalizedOptions.find((variant) => String(variant.id) === String(selectedProduct.selectedVariantId)) ||
        normalizedOptions.find((variant) => String(variant.id) === String(variantData.defaultVariantId)) ||
        normalizedOptions[0];

      selectedProduct.variantsLoading = false;
      selectedProduct.variantLoadError = variantLoadError;
      selectedProduct.variantOptions = normalizedOptions;
      selectedProduct.selectedVariantId = selectedVariant ? String(selectedVariant.id) : '';
      selectedProduct.selectedVariantTitle = selectedVariant
        ? this.normalizeVariantTitle(selectedVariant.title)
        : this.normalizeVariantTitle(variantData.defaultVariantTitle);

      this.renderSelectedProducts();
      this.syncCatalogOutput();
    }

    handleQuantityInput(event) {
      const quantityInput = event.target.closest('[data-quote-selected-quantity]');
      if (!quantityInput) return;

      const productId = Number(quantityInput.dataset.productId);
      const selectedProduct = this.selectedProducts.find((product) => product.id === productId);
      if (!selectedProduct) return;

      const parsedValue = Number.parseInt(quantityInput.value, 10);
      selectedProduct.quantity = Number.isNaN(parsedValue) || parsedValue < 1 ? 1 : parsedValue;
      quantityInput.value = selectedProduct.quantity;

      this.syncCatalogOutput();
    }

    handleSelectedListChange(event) {
      const variantSelect = event.target.closest('[data-quote-selected-variant]');
      if (!variantSelect) return;

      const productId = Number(variantSelect.dataset.productId);
      const selectedProduct = this.selectedProducts.find((product) => product.id === productId);
      if (!selectedProduct) return;

      const selectedOption = variantSelect.options[variantSelect.selectedIndex];
      selectedProduct.selectedVariantId = variantSelect.value;
      selectedProduct.selectedVariantTitle = selectedOption
        ? this.normalizeVariantTitle(selectedOption.textContent)
        : 'Default';

      this.syncCatalogOutput();
    }

    handleSelectedListClick(event) {
      const removeButton = event.target.closest('[data-quote-remove-product]');
      if (!removeButton) return;

      const productId = Number(removeButton.dataset.productId);
      this.selectedProducts = this.selectedProducts.filter((product) => product.id !== productId);

      this.renderSelectedProducts();
      this.syncCatalogOutput();

      if (this.searchInput.value.trim().length >= 2) {
        this.searchCatalogProducts(this.searchInput.value.trim());
      }
    }

    renderSelectedProducts() {
      if (!this.selectedProducts.length) {
        this.selectedList.innerHTML = '';
        if (this.selectedEmpty) {
          this.selectedEmpty.hidden = false;
        }
        return;
      }

      if (this.selectedEmpty) {
        this.selectedEmpty.hidden = true;
      }

      const selectedMarkup = this.selectedProducts
        .map((product, index) => {
          const quantityId = `QuoteSelectedQty-${product.id}-${index}`;
          const variantId = `QuoteSelectedVariant-${product.id}-${index}`;
          const productUrl = product.url ? escapeHtml(product.url) : '';
          const productName = escapeHtml(product.title);
          const productNameMarkup = productUrl
            ? `<a href="${productUrl}" target="_blank" rel="noopener">${productName}</a>`
            : productName;
          const selectedVariantTitle = escapeHtml(product.selectedVariantTitle || 'Default');
          let variantContent = `<p class="quote-request__variant-static">Variant: ${selectedVariantTitle}</p>`;

          if (product.variantsLoading) {
            variantContent = '<p class="quote-request__variant-status">Loading variants...</p>';
          } else if (Array.isArray(product.variantOptions) && product.variantOptions.length > 1) {
            const variantOptionsMarkup = product.variantOptions
              .map((variantOption) => {
                const selectedAttribute =
                  String(variantOption.id) === String(product.selectedVariantId) ? ' selected' : '';
                const variantLabel = `${this.normalizeVariantTitle(variantOption.title)}${
                  variantOption.available ? '' : ' (Unavailable)'
                }`;
                return `<option value="${escapeHtml(String(variantOption.id))}"${selectedAttribute}>${escapeHtml(variantLabel)}</option>`;
              })
              .join('');

            variantContent = `
              <label class="quote-request__variant-label" for="${variantId}">Variant</label>
              <div class="select">
                <select
                  id="${variantId}"
                  class="select__select"
                  data-quote-selected-variant
                  data-product-id="${product.id}"
                >
                  ${variantOptionsMarkup}
                </select>
              </div>
            `;
          } else if (product.variantLoadError) {
            variantContent = `
              <p class="quote-request__variant-static">Variant: ${selectedVariantTitle}</p>
              <p class="quote-request__variant-status">Variant list unavailable.</p>
            `;
          }

          const variantMarkup = `<div class="quote-request__selected-variant">${variantContent}</div>`;

          return `
            <li class="quote-request__selected-item">
              <div class="quote-request__selected-details">
                <p class="quote-request__selected-name">${productNameMarkup}</p>
              </div>
              ${variantMarkup}
              <div class="quote-request__selected-qty">
                <div class="field">
                  <input
                    id="${quantityId}"
                    class="field__input"
                    type="number"
                    min="1"
                    step="1"
                    value="${product.quantity}"
                    data-quote-selected-quantity
                    data-product-id="${product.id}"
                  >
                  <label class="field__label" for="${quantityId}">Qty</label>
                </div>
              </div>
              <button
                type="button"
                class="button button--tertiary quote-request__selected-remove"
                data-quote-remove-product
                data-product-id="${product.id}"
              >
                Remove
              </button>
            </li>
          `;
        })
        .join('');

      this.selectedList.innerHTML = selectedMarkup;
    }

    syncCatalogOutput() {
      this.catalogOutput.value = this.selectedProducts
        .map((product, index) => {
          const productUrl = toAbsoluteUrl(product.url);
          const variantLabel = product.selectedVariantTitle || (product.variantsLoading ? 'Loading...' : 'Default');
          return `${index + 1}. ${product.title} | Variant: ${variantLabel} | Qty: ${product.quantity}${
            productUrl ? ` | ${productUrl}` : ''
          }`;
        })
        .join('\n');
    }

    clearSearchResults() {
      this.searchResults.hidden = true;
      this.searchResults.innerHTML = '';
    }

    handleDocumentClick(event) {
      if (!this.catalogGroup || this.catalogGroup.contains(event.target)) return;
      this.clearSearchResults();
    }
  }

  function initQuoteRequestForms(root = document) {
    root.querySelectorAll('[data-quote-request-form]').forEach((formElement) => {
      if (initializedForms.has(formElement)) return;
      initializedForms.add(formElement);
      new QuoteRequestForm(formElement);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initQuoteRequestForms());
  } else {
    initQuoteRequestForms();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initQuoteRequestForms(event.target);
  });
})();
